import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { WritableSelect } from '../../components/ui/writable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import type { CarBrand, CarModel } from '../../types/car';

export interface CarOfDara {
  id: number;
  brand: string;
  model: string;
  color: string;
  year: number;
  chassisNo: string | null;
  plateNumber: string | null;
  plateStatus: 'TEMPORARY' | 'PERMANENT' | null;
  engineNo: string | null;
  mileage: number | null;
  dateOfRegistration: string | null;
  soldTo: string | null;
  buyerPhoneNo: string | null;
  proxiedTo: string | null;
  proxyPhoneNo: string | null;
  carOf: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function CarsOfDaraPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [models, setModels] = useState<CarModel[]>([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [form, setForm] = useState({
    brand: '',
    model: '',
    color: '',
    year: new Date().getFullYear(),
    chassisNo: '',
    plateNumber: '',
    plateStatus: '' as '' | 'TEMPORARY' | 'PERMANENT',
    dateOfRegistration: '',
    soldTo: '',
    buyerPhoneNo: '',
    proxiedTo: '',
    proxyPhoneNo: '',
    carOf: 'Dara',
  });

  const currentBrandId = selectedBrand ? brands.find((b) => b.name === selectedBrand)?.id ?? 0 : 0;

  useEffect(() => {
    const fetchRefData = async () => {
      try {
        const [brandsRes, modelsRes] = await Promise.all([
          api.get('/api/brands'),
          api.get('/api/models'),
        ]);
        setBrands(Array.isArray(brandsRes) ? brandsRes : []);
        setModels(Array.isArray(modelsRes) ? modelsRes : []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchRefData();
  }, []);

  const createBrand = async (name: string) => {
    const newBrand = await api.post('/api/brands', { name, active: true });
    setBrands((prev) => [...prev, newBrand]);
    setSelectedBrand(newBrand.name);
    setForm((f) => ({ ...f, brand: newBrand.name }));
    setSelectedModel('');
    setForm((f) => ({ ...f, model: '' }));
  };

  const createModel = async (name: string) => {
    const newModel = await api.post('/api/models', { name, brandId: currentBrandId, active: true });
    setModels((prev) => [...prev, newModel]);
    setSelectedModel(newModel.name);
    setForm((f) => ({ ...f, model: newModel.name }));
  };

  const handleBrandChange = (name: string) => {
    setSelectedBrand(name);
    setForm((f) => ({ ...f, brand: name }));
    setSelectedModel('');
    setForm((f) => ({ ...f, model: '' }));
  };

  const handleModelChange = (name: string) => {
    setSelectedModel(name);
    setForm((f) => ({ ...f, model: name }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.brand.trim() || !form.model.trim() || !form.color.trim() || !form.year) {
      toast.error('Please fill in brand, model, color, and year');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/cars-of-dara', {
        brand: form.brand.trim(),
        model: form.model.trim(),
        color: form.color.trim(),
        year: form.year,
        chassisNo: form.chassisNo.trim() || undefined,
        plateNumber: form.plateNumber.trim() || undefined,
        plateStatus: form.plateStatus || undefined,
        dateOfRegistration: form.dateOfRegistration || undefined,
        soldTo: form.soldTo.trim() || undefined,
        buyerPhoneNo: form.buyerPhoneNo.trim() || undefined,
        proxiedTo: form.proxiedTo.trim() || undefined,
        proxyPhoneNo: form.proxyPhoneNo.trim() || undefined,
        carOf: form.carOf.trim() || 'Dara',
      });
      toast.success(t('carsOfDara.carAdded'));
      setForm({
        brand: '',
        model: '',
        color: '',
        year: new Date().getFullYear(),
        chassisNo: '',
        plateNumber: '',
        plateStatus: '',
        dateOfRegistration: '',
        soldTo: '',
        buyerPhoneNo: '',
        proxiedTo: '',
        proxyPhoneNo: '',
        carOf: 'Dara',
      });
      setSelectedBrand('');
      setSelectedModel('');
      navigate('/cars-of-dara/list');
    } catch (err: unknown) {
      toast.error((err as { error?: string })?.error ?? t('carsOfDara.carAddError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
            {t('carsOfDara.title')}
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">{t('carsOfDara.subtitle')}</p>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Add Car Form */}
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle>{t('carsOfDara.addCar')}</CardTitle>
            <CardDescription>{t('carsOfDara.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>{t('sellCar.brand')}</Label>
                <WritableSelect
                  value={selectedBrand}
                  onValueChange={handleBrandChange}
                  options={brands}
                  onCreateNew={createBrand}
                  placeholder={t('sellCar.brand')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('sellCar.model')}</Label>
                <WritableSelect
                  value={selectedModel}
                  onValueChange={handleModelChange}
                  options={models.filter((m) => m.brandId === currentBrandId)}
                  onCreateNew={createModel}
                  placeholder={t('sellCar.model')}
                  disabled={!currentBrandId}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('sellCar.color')}</Label>
                <Input
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  placeholder={t('sellCar.color')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('sellCar.year')}</Label>
                <Input
                  type="number"
                  value={form.year}
                  onChange={(e) => setForm((f) => ({ ...f, year: parseInt(e.target.value, 10) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('sellCar.chassisNo')}</Label>
                <Input
                  value={form.chassisNo}
                  onChange={(e) => setForm((f) => ({ ...f, chassisNo: e.target.value }))}
                  placeholder={t('sellCar.chassisNo')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('sellCar.plateNumber')}</Label>
                <Input
                  value={form.plateNumber}
                  onChange={(e) => setForm((f) => ({ ...f, plateNumber: e.target.value }))}
                  placeholder={t('sellCar.plateNumber')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('carsOfDara.plateStatus')}</Label>
                <Select
                  value={form.plateStatus || 'none'}
                  onValueChange={(v) => setForm((f) => ({ ...f, plateStatus: v === 'none' ? '' : (v as 'TEMPORARY' | 'PERMANENT') }))}
                >
                  <SelectTrigger className="border-slate-200">
                    <SelectValue placeholder={t('carsOfDara.plateStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-</SelectItem>
                    <SelectItem value="TEMPORARY">{t('carsOfDara.plateStatusTemporary')}</SelectItem>
                    <SelectItem value="PERMANENT">{t('carsOfDara.plateStatusPermanent')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('carsOfDara.dateOfRegistration')}</Label>
                <Input
                  type="date"
                  value={form.dateOfRegistration}
                  onChange={(e) => setForm((f) => ({ ...f, dateOfRegistration: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('carsOfDara.carOf')}</Label>
                <Input
                  value={form.carOf}
                  onChange={(e) => setForm((f) => ({ ...f, carOf: e.target.value }))}
                  placeholder="Dara"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('carsOfDara.soldTo')}</Label>
                <Input
                  value={form.soldTo}
                  onChange={(e) => setForm((f) => ({ ...f, soldTo: e.target.value }))}
                  placeholder={t('carsOfDara.soldTo')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('carsOfDara.buyerPhoneNo')}</Label>
                <Input
                  value={form.buyerPhoneNo}
                  onChange={(e) => setForm((f) => ({ ...f, buyerPhoneNo: e.target.value }))}
                  placeholder={t('carsOfDara.buyerPhoneNo')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('carsOfDara.proxiedTo')}</Label>
                <Input
                  value={form.proxiedTo}
                  onChange={(e) => setForm((f) => ({ ...f, proxiedTo: e.target.value }))}
                  placeholder={t('carsOfDara.proxiedTo')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('carsOfDara.proxyPhoneNo')}</Label>
                <Input
                  value={form.proxyPhoneNo}
                  onChange={(e) => setForm((f) => ({ ...f, proxyPhoneNo: e.target.value }))}
                  placeholder={t('carsOfDara.proxyPhoneNo')}
                />
              </div>
              <div className="md:col-span-2 flex items-end">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-4 py-2.5 text-sm font-medium"
                >
                  {submitting ? t('carsOfDara.adding') : t('carsOfDara.addCar')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
